import i18n from 'localization/i18nextConf';
import toast from 'react-hot-toast';

const BASE_URL = import.meta.env.VITE_API_URL;
const appBaseUrl = import.meta.env.VITE_APP_BASE_URL;

// Add a flag to track if logout process is already initiated
let isLoggingOut = false;

interface ApiResponse {
	success: boolean;
	data?: any;
	message?: string;
	status?: number;
}

const request = async (url: string, options: any = {}): Promise<ApiResponse> => {
	const storageData = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')!) : {};
	const token = storageData?.state?.userInfo?.token ?? '';

	try {
		const activeLanguage = i18n.language ?? 'en';

		options.headers = {
			...options.headers,
			'Accept-Language': activeLanguage,
			'X-Jobstas-Version': '1.0.0',
			'X-Jobstas-Platform': 'web',
		};

		if (token) {
			options.headers = {
				...options.headers,
				Authorization: `Bearer ${token}`,
			};
		}

		const response = await fetch(url, options);

		// If unauthorized and not already logging out, handle the logout
		if (response.status === 401 && !isLoggingOut) {
			handleLogout(await response.json());
			return {
				success: false,
				status: 401,
			};
		}

		// Handle non-JSON responses
		const contentType = response.headers.get('content-type');
		if (contentType && contentType.includes('application/json')) {
			const jsonResponse = await response.json();
			return {
				...jsonResponse,
				status: response.status,
			};
		}

		return {
			success: false,
			status: response.status,
			message: 'Invalid response format',
		};
	} catch (error: any) {
		return {
			success: false,
			message: `API request failed: ${error.message}`,
		};
	}
};

// Handle logout process
const handleLogout = (response: any) => {
	if (isLoggingOut) return; // Prevent multiple executions

	isLoggingOut = true;
	toast.remove(); // Remove any existing toasts

	const message = response?.message || 'Your session has expired. Please login again.';
	toast.error(message, {
		duration: 3000,
		position: 'top-center',
	});

	// Clear auth data
	localStorage.removeItem('auth');

	// Redirect after a short delay
	setTimeout(() => {
		window.location.href = `${appBaseUrl}/login`;
		isLoggingOut = false; // Reset the flag after redirect
	}, 2000);
};

// Regular error toast for non-auth errors
const showErrorToast = (message: string) => {
	if (!isLoggingOut) {
		// Only show error toasts if not logging out
		toast.remove();
		toast.error(message, {
			duration: 3000,
			position: 'top-center',
		});
	}
};

export const handleError = async (response: ApiResponse) => {
	const { status, message } = response ?? {};

	if (status === 401) {
		// Skip showing error if already logging out
		return;
	}

	switch (status) {
		case 403:
			showErrorToast(message || 'Access forbidden');
			break;

		default:
			if (message) {
				showErrorToast(message);
			}
	}
};

// Rest of the code (get, post, put, del methods) remains the same
export const get = (url: string, options: any = {}) => request(`${BASE_URL}${url}`, options);

export const post = (
	url: string,
	data?: any,
	options: any = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
	},
) => {
	const payload = { ...data };
	return request(`${BASE_URL}${url}`, {
		...options,
		body: JSON.stringify(payload),
	});
};

export const put = (
	url: string,
	data?: any,
	options: any = {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
		},
	},
) => {
	const payload = { ...data };
	return request(`${BASE_URL}${url}`, {
		...options,
		body: JSON.stringify(payload),
	});
};

export const del = (
	url: string,
	data?: any,
	options: any = {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json',
		},
	},
) => {
	const payload = { ...data };
	return request(`${BASE_URL}${url}`, {
		...options,
		body: JSON.stringify(payload),
	});
};

export const handleResponse = (response: ApiResponse) => {
	if (!response?.success) {
		handleError(response);
		return;
	}
	return response?.data ? response.data : response;
};

export const changeLanguage = (language: string) => {
	i18n
		.changeLanguage(language)
		.then(() => {
			console.log(`Language changed to ${language}`);
		})
		.catch((error) => {
			console.error('Language change failed:', error);
		});
};
